// Pure derivation of human-readable rule statements from a variant spec.
// No React, no DOM — runs identically in the browser and in the Node prerender.
// Each statement is an i18n key (+ optional interpolation values); the consumer
// resolves it with `t(stmt.key, stmt.values)`.

import type { VariantSpec } from '@draughts/shared';

/**
 * Thematic bucket for a statement. `glance` facts (board size, piece count, who
 * starts) are surfaced as at-a-glance stats; the rest group into the how-to-play
 * theme cards. Purely presentational metadata — does not affect which keys emit.
 */
export type RuleCategory = 'glance' | 'movement' | 'capture' | 'promotion';

export interface RuleStatement {
  key: string;
  category: RuleCategory;
  values?: Record<string, string | number>;
}

/**
 * Ordered list of rule facts for a variant. One statement per concept; the
 * Yes/No choice picks a distinct key so translators write natural sentences.
 * Conditional facts are omitted (not negated) when not applicable.
 */
export function deriveRuleStatements(spec: VariantSpec): RuleStatement[] {
  const { board, rules } = spec;
  const out: RuleStatement[] = [];

  // 1. Board
  if (board.playableSquares === 'all') {
    out.push({ key: 'rules.fact.boardAllSquares', category: 'glance', values: { size: board.size } });
  } else {
    out.push({ key: 'rules.fact.board', category: 'glance', values: { size: board.size } });
  }

  // 2. Pieces per side
  out.push({ key: 'rules.fact.pieces', category: 'glance', values: { count: board.piecesPerPlayer } });

  // 3. Who starts
  out.push({ key: board.blackStarts ? 'rules.fact.startsBlack' : 'rules.fact.startsWhite', category: 'glance' });

  // 4. Movement direction
  out.push({
    key: rules.movement === 'orthogonal' ? 'rules.fact.moveOrthogonal' : 'rules.fact.moveDiagonal',
    category: 'movement',
  });

  // 5. Capture geometry
  out.push({
    key:
      rules.captureType === 'mixed'
        ? 'rules.fact.captureMixed'
        : rules.captureType === 'orthogonal'
        ? 'rules.fact.captureOrthogonal'
        : 'rules.fact.captureDiagonal',
    category: 'capture',
  });

  // 6. Men capturing backward
  out.push({
    key: rules.menCaptureBackward ? 'rules.fact.menCaptureBackwardYes' : 'rules.fact.menCaptureBackwardNo',
    category: 'capture',
  });

  // 7. King range
  out.push({ key: rules.flyingKings ? 'rules.fact.kingFlying' : 'rules.fact.kingShort', category: 'movement' });

  // 8. Men capturing kings
  out.push({
    key: rules.menCanCaptureKing ? 'rules.fact.menCaptureKingYes' : 'rules.fact.menCaptureKingNo',
    category: 'capture',
  });

  // 9. Mandatory / maximum capture
  if (!rules.mustCaptureMaximum) {
    // Capture may still be mandatory but with free choice of which (no maximum).
    out.push({ key: 'rules.fact.captureMandatoryFree', category: 'capture' });
  } else if (rules.captureCompareStrategy === 'maxValue') {
    // Only a genuinely weighted rule (Frisian/Frysk) maximizes total value. The
    // weights (man=100/king=199) are an internal encoding of "n kings are worth
    // more than 2n-1 men but less than 2n men" — the statement is qualitative,
    // the raw numbers would only confuse the reader.
    out.push({ key: 'rules.fact.maxCaptureValue', category: 'capture' });
  } else {
    // maxPieces and maxWithKings both maximize the NUMBER of pieces; the king-based
    // tiebreaks of maxWithKings are surfaced by the two statements below.
    out.push({ key: 'rules.fact.maxCapturePieces', category: 'capture' });
  }

  // 10. King priority on a tie: some variants (Italian/Triestine) must capture WITH a king.
  if (rules.preferKingCaptureOnTie) {
    out.push({ key: 'rules.fact.kingPriorityTie', category: 'capture' });
  }

  // 10b. Quality tiebreak: maxWithKings variants (Italian/Spanish/Portuguese/Triestine) break
  // ties on equal piece count by capturing the MOST kings.
  if (rules.captureCompareStrategy === 'maxWithKings') {
    out.push({ key: 'rules.fact.maxKingsCaptureTie', category: 'capture' });
  }

  // 10c. Order tiebreak: with everything else equal, take the sequence that meets
  // a king EARLIEST (Italian/Triestine). Emitted after 10b so the card lists the
  // precedence levels in the same order the engine applies them — see the filter
  // chain in packages/core/src/moves/capture-moves.ts.
  if (rules.compareCaptureSequence) {
    out.push({ key: 'rules.fact.firstKingFirst', category: 'capture' });
  }

  // 11. Huffing
  if (rules.huffing) {
    out.push({ key: 'rules.fact.huffing', category: 'capture' });
  }

  // 11b. Immediate removal of captured pieces (Turkish/Triestine). Elsewhere the
  // captured piece stays on the board until the move ends (coup turc) — that
  // default is not stated; only the exception is.
  if (rules.removesCapturedImmediately) {
    out.push({ key: 'rules.fact.captureImmediateRemoval', category: 'capture' });
  }

  // 11c. 180° reversal ban within a multi-capture (Turkish).
  if (rules.forbidCaptureReversal) {
    out.push({ key: 'rules.fact.captureNoReversal', category: 'capture' });
  }

  // 12. Promotion during capture. Three distinct behaviours:
  //  - stops: promote and end the move (Italian/English/…)
  //  - promote in passing: become a king mid-capture and continue as a king (Russian)
  //  - continue as man: cross the back rank still a man, promote only if the move ends there (International/Brazilian/Pool)
  out.push({
    key: rules.promotionStopsCapture
      ? 'rules.fact.promotionStops'
      : rules.promoteInPassing
      ? 'rules.fact.promotionInPassing'
      : 'rules.fact.promotionContinues',
    category: 'promotion',
  });

  // 13. Frisian blocked-king rule
  if (rules.blockedKingMaxMoves > 0) {
    out.push({ key: 'rules.fact.blockedKing', category: 'promotion', values: { n: rules.blockedKingMaxMoves } });
  }

  // 14. Frisian endgame draw rules
  if (rules.frisianEndgameDraws) {
    out.push({ key: 'rules.fact.frisianEndgame', category: 'promotion' });
  }

  // 14b. Turkish endgame adjudication: lone king vs lone man wins.
  if (rules.loneKingVsLoneManWins) {
    out.push({ key: 'rules.fact.loneKingWins', category: 'promotion' });
  }

  // 14c. Ghanaian rule: a player left with a single piece loses immediately.
  if (rules.singlePieceLoses) {
    out.push({ key: 'rules.fact.singlePieceLoses', category: 'promotion' });
  }

  // 15. Draw move limit. drawMoveLimit is stored in half-moves (plies); the rule
  // is conventionally stated in full moves per player, so halve it for display.
  if (rules.drawMoveLimit > 0) {
    out.push({
      key: 'rules.fact.drawLimit',
      category: 'promotion',
      values: { n: Math.round(rules.drawMoveLimit / 2) },
    });
  }

  return out;
}
